import type { AttentionItem, WorkspaceCabinet } from "@wms/domain";

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-30, CW-23): where a row of «Требует внимания» opens. Only known records and
 * routes of this application: the server names a record (entity + id), the screen builds the address from this
 * whitelist — never a URL taken from data. Anything else stays plain text without a link.
 */

/** Records of the staff application whose card is the generic `/:entity/:id` page (EntityFormRoute). */
export const STAFF_TARGETS: ReadonlySet<string> = new Set([
  "expected_receipts",
  "shipment_orders",
  "stock_counts",
  "dock_slots",
  "batches",
  "tasks",
  "task_lists",
  "waves",
  "cargo_units",
  "replenishment_rules",
  "webhook_deliveries",
  "integration_drafts",
  "zones",
  "locations",
]);

/** Records of the client cabinet and the page of the cabinet that opens each (UXW-30: never a staff card). */
const PORTAL_TARGETS: Record<string, (id: string) => string> = {
  requests: (id) => `/portal/requests?open=${id}`,
  invoices: (id) => `/portal/finance?tab=invoices&open=${id}`,
  acts: (id) => `/portal/finance?tab=acts&open=${id}`,
  receipts: (id) => `/portal/operations?kind=receipts&open=${id}`,
  orders: (id) => `/portal/operations?kind=orders&open=${id}`,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Target = Pick<NonNullable<AttentionItem["target"]>, "entity" | "id"> | null | undefined;

/** The address of a record in the given cabinet, or null when it is not a known record. */
export function targetPath(cabinet: WorkspaceCabinet, target: Target): string | null {
  if (!target || typeof target.entity !== "string" || typeof target.id !== "string" || !UUID.test(target.id)) return null;
  if (cabinet === "portal") {
    const build = Object.prototype.hasOwnProperty.call(PORTAL_TARGETS, target.entity) ? PORTAL_TARGETS[target.entity] : undefined;
    return build ? build(target.id) : null;
  }
  return STAFF_TARGETS.has(target.entity) ? `/${target.entity}/${target.id}` : null;
}

/** What the row is called: the number of a document or a task, else the name of the object (UXW-28). */
export function objectLabel(item: AttentionItem): string {
  return item.object.number ?? item.object.name ?? item.object.type;
}
